import {HydratedRouter} from 'react-router/dom'

import {startTransition, StrictMode} from 'react'
import {hydrateRoot} from 'react-dom/client'
import ReactGA from '@core/utils/reactGA'
import {markInternalTraffic} from '@core/utils/analytics'
import {routerPaths} from '../routes.ts'

// GA4 must be initialized before the first commit, not from a root useEffect: React runs child
// effects before parent effects, so any component mounted in the first commit that fires an event
// from its OWN mount effect (AgeGate's age_gate_shown, the gate_hit on a direct /auth/login?gate=…
// load, pack_ready_seen, model_ready_seen) was calling track() before root.tsx's effect had run.
// isTrackingEnabled() (@core/utils/analytics.ts) is env-based, not initialize-based, so the call
// went through react-ga4's _gtag and pushed onto window.dataLayer before gtag('js')/gtag('config',
// ID) existed to configure a target — gtag.js silently dropped it. Measured: ovid age_gate_passed
// 307 vs age_gate_shown 0, Aug 4-25 (thinking/threads/analytics_tracking.md, 2026-08-25 entry).
// window.ENV is set by an inline (non-module) script rendered in root.tsx's Document body, before
// <Scripts/> — that script runs synchronously during HTML parsing, well before this deferred module
// script executes, so it is populated here. send_page_view: false is load-bearing, moved verbatim
// from the deleted root.tsx effect: a bare initialize() issues its own gtag('config', ID), which
// sends a page_view by default: every navigation was counted twice, since the route-change effect
// in root.tsx is this app's only page_view emitter (see that effect's own comment for the 2026-08-15
// double-counted screenPageViews incident this guards against).
if (window.ENV?.GA_MEASUREMENT_ID && window.ENV?.ENV === 'production') {
    ReactGA.initialize(window.ENV.GA_MEASUREMENT_ID, {gtagOptions: {send_page_view: false}})
    // Decision 7 (`2026_09_21_wall_gaps.md`, `thinking/threads/analytics_tracking.md`): an
    // internal (founder) load's `traffic_type: 'internal'` used to be set from root.tsx's own
    // useEffect, which — same mechanism as the initialize() move above — runs AFTER the first
    // commit's own child events (`wall_search`, a direct-load `gate_hit`, …), so those hits shipped
    // untagged and reached GA4's "Internal Traffic" ingestion filter. `window.ENV.isInternalUser`
    // is embedded by root.tsx's own inline script (same vehicle as `window.ENV` itself), present
    // before this module runs — seeded here so it precedes every event this load can fire.
    if (window.ENV.isInternalUser) markInternalTraffic()
}

startTransition(() => {
    hydrateRoot(
        document,
        <StrictMode>
            <HydratedRouter/>
        </StrictMode>,
    )
})

window.addEventListener('focus', async () => {
    const response = await fetch(routerPaths['/health'])
    if (response.status === 200) {
        const json = await response.json()
        if (json.build !== window.ENV.BUILD_VERSION && json.shouldRefreshIfVersionMismatch) {
            window.location.reload()
        }
    }
})
